/**
 * Modal Component
 * Reusable modal dialog with header, body, and footer
 */
import { Component, Input, Output, EventEmitter, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';

export type ModalSize = 'sm' | 'md' | 'lg' | 'xl' | 'full';

@Component({
  selector: 'app-modal',
  standalone: true,
  imports: [CommonModule],
  host: {
    '[attr.title]': 'null',
  },
  template: `
    <div *ngIf="isOpen" class="pe-overlay" (click)="onBackdropClick($event)">
      <div class="pe-overlay__backdrop"></div>
      <div class="pe-overlay__center">
        <div class="pe-dialog" [class]="'pe-dialog--' + size" (click)="$event.stopPropagation()">
          <div class="pe-dialog__head">
            <div>
              <h3>{{ heading }}</h3>
              <p *ngIf="subtitle">{{ subtitle }}</p>
            </div>
            <button *ngIf="showCloseButton" type="button" class="pe-dialog__close" (click)="close()" aria-label="Close">
              <svg width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                <path d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
          </div>
          <div class="pe-dialog__body">
            <ng-content></ng-content>
          </div>
          <div *ngIf="hasFooter" class="pe-dialog__foot">
            <ng-content select="[modal-footer]"></ng-content>
          </div>
        </div>
      </div>
    </div>
  `,
  styles: [`
    .pe-overlay {
      position: fixed;
      inset: 0;
      z-index: 50;
      overflow: hidden;
    }
    .pe-overlay__backdrop {
      position: absolute;
      inset: 0;
      background: rgba(15, 23, 42, 0.48);
      backdrop-filter: blur(4px);
    }
    .pe-overlay__center {
      position: relative;
      min-height: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 1.25rem;
      overflow: hidden;
    }
    .pe-dialog {
      width: 100%;
      background: #fff;
      border: 1.5px solid #d4dce6;
      border-radius: 1.25rem;
      box-shadow: 0 24px 50px rgba(15, 23, 42, 0.2);
      display: flex;
      flex-direction: column;
      max-height: 88vh;
      overflow: hidden;
    }
    .pe-dialog--sm { max-width: 24rem; }
    .pe-dialog--md { max-width: 28rem; }
    .pe-dialog--lg { max-width: 36rem; }
    .pe-dialog--xl { max-width: 42rem; }
    .pe-dialog--full { max-width: 56rem; }
    .pe-dialog__head {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: 1rem;
      padding: 1.15rem 1.35rem 1rem;
      border-bottom: 1px solid #eef2f7;
    }
    .pe-dialog__head h3 {
      margin: 0;
      font-size: 1.05rem;
      font-weight: 750;
      letter-spacing: -0.02em;
      color: #0f172a;
    }
    .pe-dialog__head p {
      margin: 0.25rem 0 0;
      font-size: 0.8125rem;
      color: #64748b;
    }
    .pe-dialog__close {
      width: 2rem;
      height: 2rem;
      border: 0;
      border-radius: 0.6rem;
      background: transparent;
      color: #64748b;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
    }
    .pe-dialog__close svg {
      transition: transform 0.2s ease;
    }
    .pe-dialog__close:hover {
      background: #f1f5f9;
      color: #0f172a;
    }
    .pe-dialog__close:hover svg {
      transform: rotate(90deg);
    }
    .pe-dialog__body {
      padding: 1.15rem 1.35rem 1.25rem;
      min-height: 0;
      flex: 1;
      overflow-y: auto;
      scrollbar-width: none;
      -ms-overflow-style: none;
    }
    .pe-dialog__body::-webkit-scrollbar {
      width: 0;
      height: 0;
      display: none;
    }
    .pe-dialog__foot {
      display: flex;
      align-items: center;
      justify-content: flex-end;
      gap: 0.55rem;
      padding: 0.9rem 1.35rem;
      border-top: 1px solid #eef2f7;
      background: #f8fafc;
    }
  `]
})
export class ModalComponent {
  @Input() isOpen = false;
  @Input() heading = '';
  @Input() subtitle = '';
  @Input() size: ModalSize = 'md';
  @Input() showCloseButton = true;
  @Input() closeOnBackdrop = true;
  @Input() hasFooter = true;
  @Output() closed = new EventEmitter<void>();

  @HostListener('document:keydown.escape')
  onEscapeKey(): void {
    if (this.isOpen) {
      this.close();
    }
  }

  onBackdropClick(event: MouseEvent): void {
    if (!this.closeOnBackdrop) {
      return;
    }
    const el = event.target as HTMLElement;
    if (
      el.classList.contains('pe-overlay') ||
      el.classList.contains('pe-overlay__backdrop') ||
      el.classList.contains('pe-overlay__center')
    ) {
      this.close();
    }
  }

  close(): void {
    this.isOpen = false;
    this.closed.emit();
  }
}
